import { CalculatorExpression } from '@st/util/calculator-expression'
import { CalculatorTapeAnnotation, CalculatorTapeLineItem } from '@st/sdk'

export type EdgeInsets = { left: number; top: number; right: number; bottom: number }
export type Size = { width: number; height: number }

export type CalculatorTapeLayout = {
  container: {
    width: number
    padding: EdgeInsets
  }
  title: {
    height: number
    fontSize: number
  }
  row: {
    height: number
    fontSize: number
  }
  column: {
    gap: number
    leftFlex: number
    rightFlex: number
  }
  dividerHeight: number
}

/**
 * Layout constants for calculator-tape annotations.  Keeping all visual
 * measurements in one place guarantees that the view component, editor, and
 * size-calculation utilities stay in sync.
 */
export const CALCULATOR_TAPE_LAYOUT: CalculatorTapeLayout = {
  container: {
    width: 140,
    padding: { left: 14, top: 14, right: 14, bottom: 14 }
  },
  title: {
    height: 14,
    fontSize: 10
  },
  row: {
    height: 14,
    fontSize: 8
  },
  column: {
    gap: 2,
    leftFlex: 2,
    rightFlex: 1
  },
  dividerHeight: 10
}

/**
 * Returns the exact `Size` that the tape will occupy when rendered.
 * Height is a function of the number of line-items.
 */
export function calculateCalculatorTapeSize(tape: CalculatorTapeAnnotation): Size {
  const rowHeight = CALCULATOR_TAPE_LAYOUT.row.height
  const { dividerHeight } = CALCULATOR_TAPE_LAYOUT
  const containerPadding = CALCULATOR_TAPE_LAYOUT.container.padding

  const contentRows = 1 + tape.lineItems.length // title row + N line-items
  const height =
    containerPadding.top +
    containerPadding.bottom +
    /* space between title and first divider */ dividerHeight +
    /* line-items */ rowHeight * contentRows +
    /* divider before total */ dividerHeight +
    /* total row */ rowHeight

  return { width: CALCULATOR_TAPE_LAYOUT.container.width, height }
}

/**
 * Re-calculates totals and bounds.  Optionally strips empty line-items.
 */
export function rebuildCalculatorTape(
  tape: CalculatorTapeAnnotation,
  opts?: { removeEmptyLineItems?: boolean }
): CalculatorTapeAnnotation {
  const sanitizedTape = opts?.removeEmptyLineItems ? removeEmptyLineItems(tape) : tape

  const { width, height } = calculateCalculatorTapeSize(sanitizedTape)

  // Fast path: no items ➜ total 0.
  if (sanitizedTape.lineItems.length === 0) {
    return {
      ...sanitizedTape,
      bounds: { ...sanitizedTape.bounds, width, height },
      total: '0'
    }
  }

  // Keep only evaluatable amounts.
  const validAmounts = sanitizedTape.lineItems
    .map((item) => item.amount)
    .filter((amount) => amount.trim() !== '' && CalculatorExpression.eval(amount) !== undefined)

  const total =
    validAmounts.length === 0
      ? '0'
      : CalculatorExpression.eval({
          op: '+',
          children: validAmounts.map((value) => ({ op: 'number', value }))
        }) ?? '0'

  return {
    ...sanitizedTape,
    bounds: { ...sanitizedTape.bounds, width, height },
    total
  }
}

/**
 * Appends a new line-item to the tape given free-form input.  The input may be
 * a lone expression (e.g. "19.95") or a description followed by an expression
 * (e.g. "USB Cable 19.95").
 */
export function addLineItemToTape(
  tape: CalculatorTapeAnnotation,
  inputStr: string
): CalculatorTapeAnnotation {
  const trimmed = inputStr.trim()
  const lastSpace = trimmed.lastIndexOf(' ')

  const description = lastSpace !== -1 ? trimmed.substring(0, lastSpace).trim() : ''
  const expr = lastSpace !== -1 ? trimmed.substring(lastSpace + 1).trim() : trimmed

  const result = CalculatorExpression.eval(expr)
  if (result === undefined) return tape // invalid expression – no-op

  const newItem: CalculatorTapeLineItem = { description, amount: result }

  return rebuildCalculatorTape({ ...tape, lineItems: [...tape.lineItems, newItem] })
}

function removeEmptyLineItems(tape: CalculatorTapeAnnotation): CalculatorTapeAnnotation {
  return { ...tape, lineItems: tape.lineItems.filter((i) => i.amount !== '') }
}
